import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AssignmentOutlined,
  BiotechOutlined,
  HealingOutlined,
  MedicationOutlined,
} from "@mui/icons-material";
import { ReactNode, SyntheticEvent, useState } from "react";
import { MedicalNotes } from "./components/MedicalNotes";
import { Solicitations } from "./components/Solicitations";
import { NoteReport } from "./components/NoteReport";
import { Procedures } from "./components/Procedures";
import { IframeDialog } from "../../IframeDialog";
import { useFhirData } from "../../../../../utils/useFhirData";
import { useDocumentDialog } from "../../../../../hooks/useDocumentDialog";
import {
  processMedicalNotes,
  processSolicitations,
  processNoteReports,
  filterByCategory,
} from "../../../../../hooks/useDocumentProcessing";
import { filterBySignStatus } from "../../../../../utils/fhirDocumentUtils";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`docs-tabpanel-${index}`}
      aria-labelledby={`docs-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            pt: 1.5,
            /* Sem maxHeight/overflow aqui — a GenericTable gerencia o próprio scroll */
            animation: "fadeInTab 0.18s ease",
            "@keyframes fadeInTab": {
              from: { opacity: 0, transform: "translateY(4px)" },
              to:   { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Configuração visual de cada aba.
 * As cores referenciam o palette do theme via chave de paleta do MUI
 * para respeitar light/dark mode e personalizações.
 */
type TabPaletteKey = "primary" | "info" | "success" | "warning";

const TAB_CONFIG: {
  label: string;
  fullLabel: string;
  icon: React.ReactNode;
  paletteKey: TabPaletteKey;
}[] = [
  {
    label: "Receitas",
    fullLabel: "Receitas médicas",
    icon: <MedicationOutlined />,
    paletteKey: "primary",
  },
  {
    label: "Solicitações",
    fullLabel: "Solicitações de exame",
    icon: <BiotechOutlined />,
    paletteKey: "info",
  },
  {
    label: "Procedimentos",
    fullLabel: "Procedimentos",
    icon: <HealingOutlined />,
    paletteKey: "success",
  },
  {
    label: "Relatórios",
    fullLabel: "Relatórios e atestados",
    icon: <AssignmentOutlined />,
    paletteKey: "warning",
  },
];

interface DocTabProps {
  config: (typeof TAB_CONFIG)[number];
  count: number;
  selected: boolean;
  onClick: () => void;
  compact?: boolean;
}

/** Tab card individual com ícone, label e badge de contagem — usa cores do theme */
function DocTab({ config, count, selected, onClick, compact }: DocTabProps) {
  const theme = useTheme();
  const palette = theme.palette[config.paletteKey];
  const color = palette.main;
  const bgColor = alpha(color, 0.08);
  const borderColor = selected ? color : theme.palette.divider;

  return (
    <Box
      component="button"
      onClick={onClick}
      aria-selected={selected}
      sx={{
        flex: compact ? "0 0 auto" : 1,
        minWidth: compact ? 110 : 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1, sm: 1.5 },
        border: "2px solid",
        borderColor,
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: selected ? bgColor : "background.paper",
        transition: "all 0.18s ease",
        boxShadow: selected
          ? `0 2px 8px ${alpha(color, 0.18)}`
          : "0 1px 3px rgba(0,0,0,0.06)",
        "&:hover": {
          borderColor: color,
          bgcolor: bgColor,
          boxShadow: `0 2px 8px ${alpha(color, 0.14)}`,
        },
        position: "relative",
        outline: "none",
      }}
    >
      {/* Badge de contagem — canto superior direito */}
      <Chip
        label={count}
        size="small"
        sx={{
          position: "absolute",
          top: -8,
          right: -8,
          height: 20,
          minWidth: 20,
          fontSize: "0.65rem",
          fontWeight: 700,
          bgcolor: selected ? color : "action.disabled",
          color: selected ? palette.contrastText : "text.disabled",
          "& .MuiChip-label": { px: 0.75 },
          transition: "background-color 0.18s ease",
        }}
      />

      {/* Ícone */}
      <Box
        sx={{
          color: selected ? color : "text.secondary",
          display: "flex",
          alignItems: "center",
          "& svg": { fontSize: { xs: "1.35rem", sm: "1.6rem" } },
          transition: "color 0.18s ease",
        }}
      >
        {config.icon}
      </Box>

      {/* Label */}
      <Typography
        variant="caption"
        fontWeight={selected ? 700 : 500}
        sx={{
          color: selected ? color : "text.secondary",
          lineHeight: 1.2,
          textAlign: "center",
          fontSize: { xs: "0.65rem", sm: "0.72rem" },
          transition: "color 0.18s ease",
        }}
      >
        {config.label}
      </Typography>
    </Box>
  );
}

export function PatientDocs({
  medicalNoteData,
  solicitationData,
  resultsData,
  NoteReportData,
  setRefetch,
  sign,
  summaryData,
}: any) {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

  // Gerencia estado das tabs
  const [selectedTab, setSelectedTab] = useState(0);
  // Controla o breve loading ao trocar de aba (evita salto de tela)
  const [tabChanging, setTabChanging] = useState(false);

  const handleChange = (_: SyntheticEvent | null, newValue: number) => {
    if (newValue === selectedTab) return;
    setTabChanging(true);
    // Troca o conteúdo após 130ms — tempo suficiente para o skeleton aparecer
    setTimeout(() => {
      setSelectedTab(newValue);
      setTabChanging(false);
    }, 130);
  };

  // Gerencia estado do dialog de visualização usando hook reutilizável
  const { open, binaryUrl, setBinaryUrl, handleOpen, handleClose } =
    useDocumentDialog();

  // Extrai MedicationRequest dos dados de prescrição
  const medicationRequest = medicalNoteData?.filter(
    (el: any) => el?.resource?.resourceType === "MedicationRequest",
  );

  // Extrai ServiceRequest dos dados de solicitação
  const serviceRequest = solicitationData?.filter(
    (el: any) => el?.resource?.resourceType === "ServiceRequest",
  );

  // Processa dados de prescrições médicas
  const medicalNote = filterBySignStatus(medicalNoteData, sign);
  const rowData = processMedicalNotes(medicalNoteData, medicationRequest, sign);

  // Processa dados de solicitações de exame
  const solicitation = solicitationData?.filter((e: any) => {
    const isSigned =
      e?.resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin";
    const isSolicitation =
      e?.resource?.category?.[0]?.coding?.[0]?.display === "Solicitação";
    return sign
      ? isSigned && isSolicitation
      : e?.resource?.resourceType === "DocumentReference" &&
          !isSigned &&
          isSolicitation;
  });

  const rows = processSolicitations(
    solicitationData,
    serviceRequest,
    resultsData,
    sign,
  );

  // Processa dados de relatórios e atestados
  const noteReport = filterBySignStatus(NoteReportData, sign);
  const noteReportRowData = processNoteReports(NoteReportData, sign);

  // Filtra procedimentos por categoria
  const proceduresData = filterByCategory(solicitationData, "Procedimento");

  /** Contagens para cada aba */
  const counts = [
    medicalNote?.length ?? 0,
    solicitation?.length ?? 0,
    proceduresData?.length ?? 0,
    noteReport?.length ?? 0,
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* ── Barra de tabs moderna ── */}
      <Box
        sx={{
          overflowX: isCompact ? "auto" : "visible",
          pb: isCompact ? 0.5 : 0,
          mb: 2,
          /* Esconde scrollbar no mobile mantendo scroll funcional */
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            minWidth: isCompact ? "max-content" : "unset",
            pt: 1,     // espaço para os badges no topo não serem cortados
            px: 0.5,
          }}
        >
          {TAB_CONFIG.map((cfg, idx) => (
            <DocTab
              key={idx}
              config={cfg}
              count={counts[idx]}
              selected={selectedTab === idx}
              onClick={() => handleChange(null, idx)}
              compact={isCompact}
            />
          ))}
        </Stack>
      </Box>

      {/* Linha indicadora da aba ativa — cor via theme */}
      <Box
        sx={{
          height: "3px",
          borderRadius: 99,
          bgcolor: `${TAB_CONFIG[selectedTab].paletteKey}.main`,
          mb: 1,
          transition: "background-color 0.2s ease",
          opacity: 0.8,
        }}
      />

      {/* ── Painéis de conteúdo — altura mínima fixa evita salto ao trocar aba ── */}
      <Box sx={{ minHeight: 320, position: "relative" }}>
        {tabChanging ? (
          /* Skeleton exibido durante a transição de aba */
          <Box sx={{ pt: 1.5 }}>
            <Skeleton variant="rounded" width="100%" height={42} sx={{ mb: 1.5, borderRadius: 1.5 }} />
            <Skeleton variant="rounded" width="100%" height={42} sx={{ mb: 1.5, borderRadius: 1.5 }} />
            <Skeleton variant="rounded" width="75%"  height={42} sx={{ borderRadius: 1.5 }} />
          </Box>
        ) : (
          <>
            <CustomTabPanel value={selectedTab} index={0}>
              <MedicalNotes
                sign={sign}
                rowData={rowData}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
              />
            </CustomTabPanel>

            <CustomTabPanel value={selectedTab} index={1}>
              <Solicitations
                setRefetch={setRefetch}
                sign={sign}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
                rowData={rows}
                resultsData={resultsData}
              />
            </CustomTabPanel>

            <CustomTabPanel value={selectedTab} index={2}>
              <Procedures
                setRefetch={setRefetch}
                sign={sign}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
                data={proceduresData || []}
                resultsData={resultsData}
                procedureServiceRequests={useFhirData.extractPatientSummaryResource(
                  summaryData,
                  "ServiceRequest",
                )}
              />
            </CustomTabPanel>

            <CustomTabPanel value={selectedTab} index={3}>
              <NoteReport
                sign={sign}
                rowData={noteReportRowData}
                handleOpen={handleOpen}
                setBinaryUrl={setBinaryUrl}
              />
            </CustomTabPanel>
          </>
        )}
      </Box>

      {open && (
        <IframeDialog
          setRefetchSummary={setRefetch}
          handleClose={handleClose}
          isOpen={open}
          data={binaryUrl}
        />
      )}
    </Box>
  );
}
